import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const RULES = [
  ["collapse_multi_blank", "Collapse multiple blank lines"],
  ["strip_page_numbers", "Strip 'Page N of M' lines"],
  ["strip_standalone_numbers", "Strip standalone number lines"],
  ["repair_hyphen_line_breaks", "Repair hyphen line breaks (48- hour → 48-hour)"],
  ["reflow_lowercase_orphans", "Re-flow lowercase orphan lines back to previous paragraph"],
  ["strip_copyright_blocks", "Strip imported copyright / all-rights-reserved blocks"],
  ["strip_toc_block", "Strip imported table of contents"],
  ["trim_line_whitespace", "Trim trailing whitespace from each line"],
  ["normalize_quotes", "Normalise curly quotes to straight quotes"],
  ["normalize_dashes", "Normalise em/en-dashes to ASCII"],
  ["remove_orphan_punctuation", "Remove orphan punctuation lines (., ! ? etc.)"],
  ["boundary_detector_v2", "Boundary Detector v2 — resolve duplicate 'Chapter N' markers (TOC vs real)"],
];

export default function TabSmartFlow({ project, onChange }) {
  const [sf, setSf] = useState(project.smart_flow || { enabled: true, sensitivity: "balanced", rules: {}, custom_stopwords: "" });
  const [busy, setBusy] = useState(false);

  const toggleRule = (k, v) => setSf(prev => ({ ...prev, rules: { ...(prev.rules||{}), [k]: v } }));

  const save = async (rerun=false) => {
    setBusy(true);
    try {
      const { data } = await api.post("/ph360/settings/smart-flow", {
        enabled: !!sf.enabled,
        sensitivity: sf.sensitivity,
        rules: sf.rules || {},
        custom_stopwords: sf.custom_stopwords || "",
      });
      onChange(data);
      setSf(data.smart_flow);
      toast.success(rerun ? "Smart Flow re-applied to your manuscript" : "Smart Flow settings saved");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <h2>Smart Flow Engine</h2>
      <p>Configurable text-cleaning pipeline. Runs at import time and any time you re-save these settings.</p>
      <label className="checkbox-line">
        <input type="checkbox" checked={!!sf.enabled} onChange={(e)=>setSf({...sf,enabled:e.target.checked})} data-testid="sf-enabled"/>
        Smart Flow enabled
      </label>
      <label>Sensitivity</label>
      <select value={sf.sensitivity} onChange={(e)=>setSf({...sf,sensitivity:e.target.value})} data-testid="sf-sensitivity">
        <option value="gentle">Gentle — only obvious artifacts</option>
        <option value="balanced">Balanced — recommended</option>
        <option value="aggressive">Aggressive — reflow more, best for clean PDFs</option>
      </select>
      <h3>Rules</h3>
      {project.boundary_report && project.boundary_report.enabled !== false && (
        <div className="notice notice-info" data-testid="boundary-report" style={{margin:"6px 0"}}>
          <p style={{margin:0}}>
            <strong>Boundary Detector v2</strong> · Duplicate chapter markers found: <strong>{project.boundary_report.duplicates_found ?? 0}</strong>
            {" · "}Removed as TOC noise: <strong>{project.boundary_report.duplicates_removed ?? 0}</strong>
          </p>
        </div>
      )}
      {RULES.map(([k, label]) => (
        <label className="checkbox-line" key={k}>
          <input
            type="checkbox"
            checked={!!(sf.rules && sf.rules[k])}
            onChange={(e)=>toggleRule(k, e.target.checked)}
            data-testid={`sf-rule-${k}`}
          />
          {label}
        </label>
      ))}
      <label>Custom stopword lines to remove (comma-separated)</label>
      <input
        type="text"
        placeholder="e.g. Chapter Preview, End of Sample"
        value={sf.custom_stopwords||""}
        onChange={(e)=>setSf({...sf,custom_stopwords:e.target.value})}
        data-testid="sf-stopwords"
      />
      <p className="description">Any line matching one of these values (case-insensitive) is removed from the manuscript.</p>
      <p style={{marginTop:14}}>
        <button className="button button-primary" onClick={()=>save(true)} disabled={busy} data-testid="sf-save">
          {busy ? "Applying…" : "Save + Re-run on manuscript"}
        </button>
      </p>
    </>
  );
}
